import { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Calendar, Clock, Paperclip, LinkIcon, ArrowRight, Trash2,
  FileText, ChevronDown, ChevronRight, Upload, Star, Users, Target,
  Tag, User, Timer, CheckCircle2, BarChart3, Info,
} from 'lucide-react';
import { format } from 'date-fns';

import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { TypeBadge } from '@/components/shared/TypeBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { StarRating } from '@/components/shared/StarRating';
import { TaskStopwatch } from '@/components/tasks/TaskStopwatch';
import { EmptyState } from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  taskService,
  PHASE_STATUSES,
  type TaskDetail,
  type Attachment,
  type TimerLogEntry,
} from '@/services/task.service';

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------
function formatLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

// ===========================================================================
// Main Page
// ===========================================================================
export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  // --- Data queries ---
  const { data: task, isLoading } = useQuery({
    queryKey: ['task', id],
    queryFn: () => taskService.get(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  // Status update mutation
  const statusMut = useMutation({
    mutationFn: (status: string) => taskService.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', id] });
      toast({ title: 'Status updated' });
    },
    onError: (e: any) =>
      toast({
        title: 'Error',
        description: e.response?.data?.error?.message || 'Failed to update status',
        variant: 'destructive',
      }),
  });

  const attachmentsRef = useRef<HTMLDivElement>(null);

  // --- Loading skeleton ---
  if (isLoading) {
    return (
      <div className="space-y-4 max-w-5xl mx-auto">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-[500px]" />
      </div>
    );
  }

  if (!task) {
    return <EmptyState title="Task not found" description="The task you are looking for does not exist or has been removed." />;
  }

  const isAssignee = user?.id === task.assignee_id;
  const isDone = task.phase_status === 'done';
  const hoursProgress =
    task.estimated_hours && task.estimated_hours > 0
      ? Math.min(100, Math.round((Number(task.actual_hours) / task.estimated_hours) * 100))
      : 0;

  // Parent context label
  const parentContext = task.goal
    ? { label: 'Goal', name: task.goal.title, refId: task.goal.reference_id, link: `/goals/${task.goal.id}` }
    : task.sprint
      ? { label: 'Sprint', name: task.sprint.name, refId: task.sprint.reference_id, link: `/sprints/${task.sprint.id}` }
      : task.group
        ? { label: 'Group', name: task.group.name, refId: task.group.reference_id, link: `/groups/${task.group.id}` }
        : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* Back button                                                        */}
      {/* ------------------------------------------------------------------ */}
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      {/* ------------------------------------------------------------------ */}
      {/* Header                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {/* Row 1: Type badge + reference + title */}
          <div className="flex items-start gap-3 flex-wrap">
            <TypeBadge type={task.task_type} />
            <span className="font-mono text-xs bg-muted text-muted-foreground px-2 py-1 rounded">
              {task.reference_id}
            </span>
            {parentContext && (
              <Link
                to={parentContext.link}
                className="text-xs text-brand-primary hover:underline flex items-center gap-1"
              >
                <span className="text-muted-foreground">{parentContext.label}:</span>
                {parentContext.name}
              </Link>
            )}
          </div>

          <h1 className="text-2xl font-bold leading-tight">{task.title}</h1>

          {/* Row 2: Status badges + status changer */}
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={task.phase_status} />
            <StatusBadge status={task.calculated_status} />
            <PriorityBadge priority={task.priority} />

            <div className="ml-auto flex items-center gap-2">
              {/* Attachments count */}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => attachmentsRef.current?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attachments
              </Button>

              {/* Status change dropdown */}
              <Select value={task.phase_status} onValueChange={(v) => statusMut.mutate(v)}>
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue placeholder="Change status" />
                </SelectTrigger>
                <SelectContent>
                  {PHASE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {formatLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Timer section (only for assignee, only when not done)            */}
          {/* ---------------------------------------------------------------- */}
          {isAssignee && !isDone && user && (
            <TaskStopwatch
              taskId={task.id}
              phaseStatus={task.phase_status}
              assigneeId={task.assignee_id}
              timerStartedAt={task.timer_started_at}
              accumulatedSeconds={task.accumulated_seconds}
              currentUserId={user.id}
            />
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Timer History (collapsible)                                        */}
      {/* ------------------------------------------------------------------ */}
      {isAssignee && (
        <TimerHistory taskId={task.id} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Main content: two-column layout                                    */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              {task.description ? (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                  {task.description}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">No description provided.</p>
              )}
            </CardContent>
          </Card>

          {/* Hours section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Hours
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {Number(task.actual_hours).toFixed(1)}h logged
                  {task.estimated_hours != null && ` / ${task.estimated_hours}h estimated`}
                </span>
                {task.estimated_hours != null && task.estimated_hours > 0 && (
                  <span className="text-xs font-medium">{hoursProgress}%</span>
                )}
              </div>
              {task.estimated_hours != null && task.estimated_hours > 0 && (
                <Progress value={hoursProgress} className="h-2" />
              )}
            </CardContent>
          </Card>

          {/* Sprint Goals (if sprint task) */}
          {task.sprint_goals && task.sprint_goals.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Sprint Goals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {task.sprint_goals.map((sg) => (
                    <li key={sg.id} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      <span>{sg.title}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Dependencies section */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Dependencies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TaskDependencies task={task} />
            </CardContent>
          </Card>
        </div>

        {/* Right column (1/3) - sidebar */}
        <div className="space-y-6">
          {/* Assignee + Creator */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="h-4 w-4" />
                People
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1.5">Assignee</p>
                {task.assignee ? (
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      name={task.assignee.full_name}
                      avatarUrl={task.assignee.avatar_url}
                      size="sm"
                    />
                    <span className="text-sm font-medium">{task.assignee.full_name}</span>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                )}
              </div>
              <div className="border-t pt-3">
                <p className="text-xs text-muted-foreground mb-1.5">Creator</p>
                {task.creator ? (
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      name={task.creator.full_name}
                      avatarUrl={task.creator.avatar_url}
                      size="sm"
                    />
                    <span className="text-sm font-medium">{task.creator.full_name}</span>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">--</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Start Date</span>
                <span>{task.start_date ? format(new Date(task.start_date), 'MMM d, yyyy') : '--'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">End Date</span>
                <span>{task.end_date ? format(new Date(task.end_date), 'MMM d, yyyy') : '--'}</span>
              </div>
              {task.completed_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Completed</span>
                  <span>{format(new Date(task.completed_at), 'MMM d, yyyy')}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Evaluation Score */}
          {task.evaluation_score != null && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Evaluation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <StarRating value={task.evaluation_score} />
                  <span className="text-sm font-medium">{task.evaluation_score}/5</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {task.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: tag.color + '20',
                        color: tag.color,
                      }}
                    >
                      {tag.name}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Context links: sprint, goal, group, team */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-4 w-4" />
                Context
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {task.sprint && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Sprint</span>
                  <Link to={`/sprints/${task.sprint.id}`} className="text-brand-primary hover:underline truncate ml-2">
                    {task.sprint.name}
                  </Link>
                </div>
              )}
              {task.goal && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Goal</span>
                  <Link to={`/goals/${task.goal.id}`} className="text-brand-primary hover:underline truncate ml-2">
                    {task.goal.title}
                  </Link>
                </div>
              )}
              {task.group && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Group</span>
                  <Link to={`/groups/${task.group.id}`} className="text-brand-primary hover:underline truncate ml-2">
                    {task.group.name}
                  </Link>
                </div>
              )}
              {task.team && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Team</span>
                  <span className="truncate ml-2">{task.team.name}</span>
                </div>
              )}
              {task.column && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Column</span>
                  <span className="truncate ml-2">{task.column.name}</span>
                </div>
              )}
              {!task.sprint && !task.goal && !task.group && !task.team && !task.column && (
                <p className="text-muted-foreground italic">No linked context.</p>
              )}
            </CardContent>
          </Card>

          {/* Evaluators */}
          {task.evaluators && task.evaluators.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Evaluators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {task.evaluators.map((ev) => (
                    <div key={ev.id} className="flex items-center gap-2">
                      <UserAvatar
                        name={ev.user.full_name}
                        avatarUrl={ev.user.avatar_url}
                        size="xs"
                      />
                      <span className="text-sm">{ev.user.full_name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Metadata timestamps */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Created</span>
                <span>{format(new Date(task.created_at), 'MMM d, yyyy HH:mm')}</span>
              </div>
              <div className="flex justify-between">
                <span>Updated</span>
                <span>{format(new Date(task.updated_at), 'MMM d, yyyy HH:mm')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Bottom: Attachments tab with upload                                */}
      {/* ------------------------------------------------------------------ */}
      <Card ref={attachmentsRef}>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Attachments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TaskAttachments taskId={id!} />
        </CardContent>
      </Card>
    </div>
  );
}

// ===========================================================================
// Timer History (collapsible)
// ===========================================================================
function TimerHistory({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['task-timer-logs', taskId],
    queryFn: () => taskService.timerLogs(taskId, { limit: 50 }).then((r) => r.data.data),
    enabled: open,
  });

  const logs: TimerLogEntry[] = data ?? [];

  return (
    <Card>
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium hover:bg-muted/50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-2">
          <Timer className="h-4 w-4" />
          Timer History
        </span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && (
        <CardContent className="pt-0">
          {isLoading ? (
            <Skeleton className="h-20" />
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No timer activity yet.</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center justify-between text-sm border-b last:border-b-0 py-2"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">
                      {log.action}
                    </Badge>
                    {log.user && (
                      <span className="text-xs text-muted-foreground">{log.user.full_name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground text-xs">
                    <span className="font-mono">{formatDuration(log.accumulated_seconds)}</span>
                    <span>{format(new Date(log.created_at), 'MMM d, HH:mm:ss')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ===========================================================================
// Dependencies
// ===========================================================================
function TaskDependencies({ task }: { task: TaskDetail }) {
  const deps = task.dependencies ?? [];
  const dependents = task.dependents ?? [];

  if (deps.length === 0 && dependents.length === 0) {
    return <p className="text-sm text-muted-foreground">No dependencies.</p>;
  }

  return (
    <div className="space-y-5">
      {deps.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Depends On
          </h4>
          <div className="space-y-2">
            {deps.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-lg border p-3">
                <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                {d.depends_on ? (
                  <>
                    <span className="font-mono text-xs text-muted-foreground">
                      {d.depends_on.reference_id}
                    </span>
                    <Link
                      to={`/tasks/${d.depends_on.id}`}
                      className="truncate text-sm font-medium hover:text-brand-primary"
                    >
                      {d.depends_on.title}
                    </Link>
                    <StatusBadge status={d.depends_on.status} />
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">Unknown</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {dependents.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Depended By
          </h4>
          <div className="space-y-2">
            {dependents.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-lg border p-3">
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                {d.entity ? (
                  <>
                    <span className="font-mono text-xs text-muted-foreground">
                      {d.entity.reference_id}
                    </span>
                    <Link
                      to={`/tasks/${d.entity.id}`}
                      className="truncate text-sm font-medium hover:text-brand-primary"
                    >
                      {d.entity.title}
                    </Link>
                    <StatusBadge status={d.entity.status} />
                  </>
                ) : (
                  <span className="text-sm text-muted-foreground">Unknown</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Attachments (with upload support)
// ===========================================================================
function TaskAttachments({ taskId }: { taskId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['task-attachments', taskId],
    queryFn: () => taskService.listAttachments(taskId).then((r) => r.data.data),
  });

  const uploadMut = useMutation({
    mutationFn: (file: File) => {
      return taskService.uploadAttachment('task', taskId, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
      toast({ title: 'File uploaded' });
    },
    onError: (e: any) =>
      toast({
        title: 'Upload failed',
        description: e.response?.data?.error?.message || 'Could not upload file',
        variant: 'destructive',
      }),
  });

  const addUrlMut = useMutation({
    mutationFn: (url: string) => taskService.addUrlAttachment('task', taskId, url),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
      setUrlInput('');
      setShowUrlInput(false);
      toast({ title: 'Link added' });
    },
    onError: (e: any) =>
      toast({
        title: 'Error',
        description: e.response?.data?.error?.message || 'Could not add link',
        variant: 'destructive',
      }),
  });

  const deleteMut = useMutation({
    mutationFn: (attId: string) => taskService.deleteAttachment(attId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-attachments', taskId] });
      toast({ title: 'Attachment deleted' });
    },
  });

  const attachments: Attachment[] = data ?? [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadMut.mutate(file);
      e.target.value = '';
    }
  };

  if (isLoading) return <Skeleton className="h-32" />;

  return (
    <div className="space-y-4">
      {/* Upload controls */}
      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMut.isPending}
        >
          <Upload className="h-3.5 w-3.5" />
          {uploadMut.isPending ? 'Uploading...' : 'Upload File'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => setShowUrlInput(!showUrlInput)}
        >
          <LinkIcon className="h-3.5 w-3.5" />
          Add Link
        </Button>
      </div>

      {/* URL input */}
      {showUrlInput && (
        <div className="flex items-center gap-2">
          <input
            type="url"
            placeholder="https://..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && urlInput.trim()) {
                addUrlMut.mutate(urlInput.trim());
              }
            }}
          />
          <Button
            size="sm"
            disabled={!urlInput.trim() || addUrlMut.isPending}
            onClick={() => urlInput.trim() && addUrlMut.mutate(urlInput.trim())}
          >
            {addUrlMut.isPending ? 'Adding...' : 'Add'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setShowUrlInput(false); setUrlInput(''); }}>
            Cancel
          </Button>
        </div>
      )}

      {/* Attachment list */}
      {attachments.length === 0 ? (
        <EmptyState
          icon={Paperclip}
          title="No attachments"
          description="Upload files or add links to this task."
        />
      ) : (
        <div className="space-y-2">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center justify-between rounded-lg border p-3"
            >
              <div className="flex items-center gap-2 min-w-0">
                {att.attachment_type === 'file' ? (
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <LinkIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <div className="min-w-0">
                  {att.url ? (
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-sm font-medium text-brand-primary hover:underline block"
                    >
                      {att.file_name || att.url}
                    </a>
                  ) : (
                    <p className="truncate text-sm font-medium">
                      {att.file_name || 'Attachment'}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {att.file_size && <span>{(att.file_size / 1024).toFixed(0)} KB</span>}
                    <span>{format(new Date(att.created_at), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive shrink-0"
                onClick={() => deleteMut.mutate(att.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
